/**
 * Finance & PO Governance
 * =======================
 * Purchase order, invoicing, and financial controls.
 * 
 * Route: /wyldecrest/helpdesk/finance-governance
 */

import React from 'react';
import GovernancePageLayout, { 
  DocumentSection, 
  SOPStep, 
  Checklist, 
  EscalationPath,
  WarningBox,
  InfoBox 
} from './GovernancePageLayout';
import { 
  DollarSign, 
  FileText, 
  Shield, 
  AlertTriangle,
  CheckCircle,
  Receipt
} from 'lucide-react';

const sections = [
  { id: 'po-process', title: 'Purchase Order Process' },
  { id: 'approval-matrix', title: 'Approval Authority Matrix' },
  { id: 'invoice-validation', title: 'Invoice Validation' },
  { id: 'payment-process', title: 'Payment Process' },
  { id: 'financial-controls', title: 'Financial Controls' },
];

export default function FinanceGovernancePage() {
  return (
    <GovernancePageLayout
      title="Finance & PO Governance"
      subtitle="Purchase order, invoicing, and financial controls documentation"
      icon={DollarSign}
      version="1.8"
      lastReviewed="2026-05-21"
      reviewedBy="Finance Director"
      sections={sections}
    >
      {/* PO Process */}
      <DocumentSection title="Purchase Order Process" icon={FileText} id="po-process">
        <p className="text-slate-300 mb-6">
          All contractor works must have an approved Purchase Order before work commences.
        </p>

        <SOPStep 
          number={1}
          title="Quote Submission"
          description="Contractor submits quote via portal."
          substeps={[
            'Contractor reviews job scope',
            'Contractor enters labour, materials, and other costs',
            'Quote is submitted for review'
          ]}
        />
        
        <SOPStep 
          number={2}
          title="Quote Review"
          description="Helpdesk reviews quote for accuracy and completeness."
          substeps={[
            'Check quote against job description',
            'Verify rates are within benchmark',
            'Check materials are appropriate',
            'Add any notes or questions'
          ]}
        />
        
        <SOPStep 
          number={3}
          title="Approval Routing"
          description="Quote routed to appropriate approver based on value."
          substeps={[
            'System determines approval level required',
            'Quote sent to approver with full context',
            'Approver reviews and decides',
            'Decision logged in audit trail'
          ]}
        />

        <SOPStep 
          number={4}
          title="PO Generation"
          description="Approved quote generates Purchase Order."
          substeps={[
            'PO number auto-generated',
            'PO document created with terms',
            'PO sent to contractor',
            'Job status updated to "PO Issued"'
          ]}
        />

        <WarningBox title="No-PO Rule">
          Work must NOT commence without an approved PO. Any invoices for work done
          without a PO may be disputed or rejected.
        </WarningBox>
      </DocumentSection>

      {/* Approval Matrix */}
      <DocumentSection title="Approval Authority Matrix" icon={Shield} id="approval-matrix">
        <p className="text-slate-300 mb-6">
          Quote approvals are tiered based on value to ensure appropriate oversight.
        </p>

        <h3 className="text-lg font-semibold text-white mb-4">Approval Thresholds</h3>
        
        <div className="bg-slate-900/50 rounded-lg p-4 mb-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-400 border-b border-slate-700">
                <th className="pb-2">Value Range</th>
                <th className="pb-2">Approver</th>
                <th className="pb-2">Response SLA</th>
              </tr>
            </thead>
            <tbody className="text-slate-300">
              <tr className="border-b border-slate-800">
                <td className="py-2 text-emerald-400">≤ £500</td>
                <td>Auto-Approved</td>
                <td>Immediate</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2 text-blue-400">£501 - £2,500</td>
                <td>Team Lead / Helpdesk Manager</td>
                <td>4 hours</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2 text-amber-400">£2,501 - £10,000</td>
                <td>Operations Manager</td>
                <td>24 hours</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2 text-purple-400">£10,001 - £25,000</td>
                <td>Operations Director</td>
                <td>48 hours</td>
              </tr>
              <tr>
                <td className="py-2 text-red-400">&gt; £25,000</td>
                <td>Executive / Board</td>
                <td>5 working days</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 className="text-lg font-semibold text-white mb-4">Approval Decision Options</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-emerald-900/20 border border-emerald-700/50 rounded-lg p-4">
            <h4 className="text-emerald-300 font-semibold mb-2">Approve</h4>
            <p className="text-sm text-slate-300">
              Quote accepted. PO generated and sent to contractor.
            </p>
          </div>
          
          <div className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-4">
            <h4 className="text-amber-300 font-semibold mb-2">Request Revision</h4>
            <p className="text-sm text-slate-300">
              Quote needs changes. Returned to contractor with feedback.
            </p>
          </div>
          
          <div className="bg-red-900/20 border border-red-700/50 rounded-lg p-4">
            <h4 className="text-red-300 font-semibold mb-2">Reject</h4>
            <p className="text-sm text-slate-300">
              Quote declined. Job may be reassigned or cancelled.
            </p>
          </div>
        </div>
      </DocumentSection>

      {/* Invoice Validation */}
      <DocumentSection title="Invoice Validation" icon={Receipt} id="invoice-validation">
        <p className="text-slate-300 mb-6">
          All invoices must be validated against the approved PO before payment.
        </p>

        <SOPStep 
          number={1}
          title="Invoice Receipt"
          description="Contractor submits invoice via portal."
          substeps={[
            'Invoice uploaded with PO reference',
            'System extracts invoice details',
            'Initial validation against PO performed'
          ]}
        />
        
        <SOPStep 
          number={2}
          title="Three-Way Match"
          description="Validate invoice against PO and completion evidence."
          substeps={[
            'PO exists and is valid',
            'Job marked as completed with evidence',
            'Invoice amount matches PO (within tolerance)',
            'VAT correctly calculated'
          ]}
        />
        
        <SOPStep 
          number={3}
          title="Exception Handling"
          description="Handle any discrepancies."
          substeps={[
            'Flag invoices exceeding PO by >5%',
            'Flag invoices without completion evidence',
            'Route exceptions to appropriate reviewer',
            'Document resolution'
          ]}
        />

        <h3 className="text-lg font-semibold text-white mb-4 mt-6">Validation Rules</h3>
        
        <div className="bg-slate-900/50 rounded-lg p-4 mb-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-400 border-b border-slate-700">
                <th className="pb-2">Check</th>
                <th className="pb-2">Tolerance</th>
                <th className="pb-2">Action if Failed</th>
              </tr>
            </thead>
            <tbody className="text-slate-300">
              <tr className="border-b border-slate-800">
                <td className="py-2">PO Reference Valid</td>
                <td>Exact match</td>
                <td>Reject - Request correct PO</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2">Invoice ≤ PO Value</td>
                <td>+5%</td>
                <td>Flag for review</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2">Job Completed</td>
                <td>Must be complete</td>
                <td>Hold until completed</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2">Evidence Present</td>
                <td>Required</td>
                <td>Request evidence</td>
              </tr>
              <tr>
                <td className="py-2">VAT Calculation</td>
                <td>£1 tolerance</td>
                <td>Adjust automatically</td>
              </tr>
            </tbody>
          </table>
        </div>

        <Checklist 
          title="Invoice Validation Checklist"
          items={[
            'Valid PO reference on invoice',
            'Invoice amount within tolerance of PO',
            'Job marked as completed',
            'Completion evidence approved',
            'Contractor details match PO',
            'VAT registration number correct',
            'Bank details verified'
          ]}
        />
      </DocumentSection>

      {/* Payment Process */}
      <DocumentSection title="Payment Process" icon={DollarSign} id="payment-process">
        <p className="text-slate-300 mb-6">
          Validated invoices are processed for payment according to agreed terms.
        </p>

        <h3 className="text-lg font-semibold text-white mb-4">Payment Terms</h3>
        
        <div className="bg-slate-900/50 rounded-lg p-4 mb-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-400 border-b border-slate-700">
                <th className="pb-2">Contractor Tier</th>
                <th className="pb-2">Payment Terms</th>
                <th className="pb-2">Payment Method</th>
              </tr>
            </thead>
            <tbody className="text-slate-300">
              <tr className="border-b border-slate-800">
                <td className="py-2 text-emerald-400">Preferred</td>
                <td>14 days from approval</td>
                <td>BACS</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2 text-blue-400">Standard</td>
                <td>30 days from approval</td>
                <td>BACS</td>
              </tr>
              <tr>
                <td className="py-2 text-amber-400">New / Trial</td>
                <td>45 days from approval</td>
                <td>BACS</td>
              </tr>
            </tbody>
          </table>
        </div>

        <InfoBox title="Payment Runs">
          Payment runs are processed every Friday. Invoices approved by Wednesday 12:00
          will be included in that week's payment run.
        </InfoBox>
      </DocumentSection>

      {/* Financial Controls */}
      <DocumentSection title="Financial Controls" icon={Shield} id="financial-controls">
        <p className="text-slate-300 mb-6">
          Financial controls ensure proper governance and fraud prevention.
        </p>

        <h3 className="text-lg font-semibold text-white mb-4">Control Framework</h3>
        
        <div className="space-y-4">
          <div className="bg-emerald-900/20 border border-emerald-700/50 rounded-lg p-4">
            <h4 className="text-emerald-300 font-semibold mb-2">Segregation of Duties</h4>
            <ul className="text-sm text-slate-300 space-y-1">
              <li>• Quote creation and approval by different people</li>
              <li>• Invoice validation and payment authorization separate</li>
              <li>• Bank detail changes require dual approval</li>
            </ul>
          </div>
          
          <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-4">
            <h4 className="text-blue-300 font-semibold mb-2">Audit Trail</h4>
            <ul className="text-sm text-slate-300 space-y-1">
              <li>• All financial actions logged with user and timestamp</li>
              <li>• Quote/PO/Invoice history preserved</li>
              <li>• Approval decisions recorded with notes</li>
            </ul>
          </div>
          
          <div className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-4">
            <h4 className="text-amber-300 font-semibold mb-2">Exception Monitoring</h4>
            <ul className="text-sm text-slate-300 space-y-1">
              <li>• Weekly report of invoices exceeding PO</li>
              <li>• Monthly review of approval patterns</li>
              <li>• Quarterly audit of contractor payments</li>
            </ul>
          </div>
          
          <div className="bg-red-900/20 border border-red-700/50 rounded-lg p-4">
            <h4 className="text-red-300 font-semibold mb-2">Fraud Prevention</h4>
            <ul className="text-sm text-slate-300 space-y-1">
              <li>• Bank detail changes require verification call</li>
              <li>• Duplicate invoice detection</li>
              <li>• Unusual pattern alerts</li>
            </ul>
          </div>
        </div>
      </DocumentSection>
    </GovernancePageLayout>
  );
}
